// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Alarms home, rebuilt to the Operator Console mock: a Carbon top tab bar
// (Alarms · Anomalies · Routine · Recommendations · Closed) with count badges, a
// title, Carbon ContentSwitcher sort + layout controls, group-filter pills, and
// the card/tile list. Server state is TanStack Query (useConsoleAlarms, derived
// via select); sort + layout live in the alarmsViewStore zustand store, the group
// filter in `?group=` (see AlarmList / AlarmTabs). Closed is one mixed,
// newest-first queue of every closed kind (ClosedQueue), so it carries its own
// kind chips + search and only the layout control.

import React from 'react';
import { useParams } from 'react-router-dom';
import { PageHead } from '../../components/shell/ScreenHelpers';
import { AlarmTabs } from '../../components/shell/alarms/AlarmTabs';
import { AlarmList } from '../../components/shell/alarms/AlarmList';
import { ClosedQueue } from '../../components/shell/ClosedQueue';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useHomeTabCounts } from '../../hooks/useHomeTabCounts';
import { useAlarmsViewStore, type AlarmSort, type AlarmLayout } from '../../stores/alarmsViewStore';
import { ALARM_TABS, tabForBucket } from '../../services/alarmSelectors';

const SORTS: { key: AlarmSort; label: string; disabled?: boolean }[] = [
  { key: 'severity', label: 'Severity' },
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
];
const LAYOUTS: { key: AlarmLayout; label: string }[] = [
  { key: 'list', label: 'List' },
  { key: 'tiles', label: 'Tiles' },
];

export default function HomeAlarms() {
  const { bucket = 'alarms' } = useParams();
  // Atomic selectors — each control subscribes to only its slice.
  const layout = useAlarmsViewStore(s => s.layout);
  const setLayout = useAlarmsViewStore(s => s.setLayout);
  const sort = useAlarmsViewStore(s => s.sort);
  const setSort = useAlarmsViewStore(s => s.setSort);

  const { siteId, siteLabel } = useActiveSite();
  const activeTab = tabForBucket(bucket);
  const isClosed = activeTab === 'closed';
  const title = ALARM_TABS.find(t => t.key === activeTab)?.label ?? 'Alarms';

  // Shared badges: alarm counts, with the manager's Recommendations badge
  // overridden to the number of plans awaiting their approval.
  const counts = useHomeTabCounts();

  // Closed is ordered by close time by definition, so it offers layout only.
  const alarmControls = (
    <div className="hs-alarm-controls">
      {!isClosed && (
        <div className="hs-segmented" role="group" aria-label="Sort order">
          {SORTS.map(s => (
            <button
              key={s.key}
              type="button"
              className={`hs-pill${sort === s.key ? ' active' : ''}`}
              aria-pressed={sort === s.key}
              disabled={s.disabled}
              title={s.disabled ? 'Severity data is not available yet' : undefined}
              onClick={() => setSort(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      <div className="hs-segmented" role="group" aria-label="Layout">
        {LAYOUTS.map(l => (
          <button
            key={l.key}
            type="button"
            className={`hs-pill${layout === l.key ? ' active' : ''}`}
            aria-pressed={layout === l.key}
            onClick={() => setLayout(l.key)}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="hs-screen">
      <AlarmTabs active={activeTab} counts={counts} />

      <div className="hs-screen-body">
        <PageHead title={title} dateline={`${siteLabel} · live`} actions={alarmControls} />

        {isClosed ? (
          <ClosedQueue siteId={siteId} siteLabel={siteLabel} />
        ) : (
          <AlarmList siteId={siteId} tab={activeTab} />
        )}
      </div>
    </div>
  );
}
